import React, { useState, useEffect } from 'react';
import Layout from '../Layout/Layout';
import { getCdnImageUrl } from '../../utils/cdn';
import './AdminStudio.css';

const DEFAULT_PASSCODE = process.env.REACT_APP_ADMIN_PASSCODE || 'halftime2026';

const renderStarRatingHtml = (num, max = 5) => {
  const fullStars = Math.floor(num);
  const hasHalf = (num % 1) >= 0.25 && (num % 1) <= 0.75;
  const emptyStars = max - fullStars - (hasHalf ? 1 : 0);

  let starsStr = '★'.repeat(fullStars);
  if (hasHalf) starsStr += '½';
  starsStr += '☆'.repeat(Math.max(0, emptyStars));

  return `<span class="star-rating-badge" title="${num}/${max} Rating"><span class="star-gold">${starsStr}</span> <span class="rating-text">(${num}/${max})</span></span>`;
};

const convertRatingsToStars = (input) => {
  if (!input || typeof input !== 'string') return '';

  let result = input;

  // Rating: X/5 or Rating: X.X/5
  result = result.replace(/Rating:\s*([0-5](?:\.[0-9])?)\s*\/\s*5/gi, (match, score) => {
    const val = parseFloat(score);
    return `<strong class="rating-label">Rating:</strong> ${renderStarRatingHtml(val)}`;
  });

  // Rating: X.X or Rating: X (standalone)
  result = result.replace(/Rating:\s*([0-5](?:\.[0-9])?)\s*(?=[<,\s\n]|$)(?!\/)/gi, (match, score) => {
    const val = parseFloat(score);
    return `<strong class="rating-label">Rating:</strong> ${renderStarRatingHtml(val)}`;
  });

  // X/5 stars or X.X/5 stars
  result = result.replace(/([0-5](?:\.[0-9])?)\s*\/\s*5\s*stars?/gi, (match, score) => {
    const val = parseFloat(score);
    return renderStarRatingHtml(val);
  });

  return result;
};

/**
 * Automatically converts free plain-text narratives, line breaks,
 * bullet lists, headings, and star ratings into clean semantic HTML.
 * Preserves text if HTML tags are already present.
 * Accepts optional flags: { isFirstLineHeading, isBulletList }
 */
const formatTextToHtml = (text, flags = {}) => {
  if (!text || typeof text !== 'string') return '';
  let trimmed = text.trim();
  if (!trimmed) return '';

  trimmed = convertRatingsToStars(trimmed);

  // If text already contains HTML tags (e.g. <p>, <h3>, <ul>), return parsed ratings
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return trimmed;
  }

  const allLines = trimmed.split('\n').map(l => l.trim()).filter(Boolean);
  if (allLines.length === 0) return '';

  let htmlResult = '';
  let linesToProcess = allLines;

  // FLAG 1: Make First Line a Heading
  if (flags.isFirstLineHeading && allLines.length > 0) {
    const firstLine = allLines[0].replace(/^#{1,4}\s+/, '');
    htmlResult += `<h3 class="detail-heading">${firstLine}</h3>\n`;
    linesToProcess = allLines.slice(1);
  }

  // FLAG 2: Make Content a Bullet List
  if (flags.isBulletList && linesToProcess.length > 0) {
    const listItems = linesToProcess.map(l => {
      const cleanLine = l.replace(/^[-*•]\s+/, '');
      return `<li>${convertRatingsToStars(cleanLine)}</li>`;
    }).join('');
    return `${htmlResult}<ul>${listItems}</ul>`.trim();
  }

  // Standard processing if isBulletList flag is not explicitly checked
  if (linesToProcess.length > 0) {
    const remainingText = linesToProcess.join('\n');
    const blocks = remainingText.split(/\n\s*\n/);

    const htmlBlocks = blocks.map(block => {
      const lines = block.split('\n').map(l => l.trim()).filter(Boolean);

      // Bullet point lists (- or * or •)
      if (lines.length > 0 && lines.every(l => /^[-*•]\s+/.test(l))) {
        const listItems = lines.map(l => `<li>${convertRatingsToStars(l.replace(/^[-*•]\s+/, ''))}</li>`).join('');
        return `<ul>${listItems}</ul>`;
      }

      // Headings (# Heading)
      if (lines.length === 1 && /^#{1,4}\s+/.test(lines[0])) {
        const headingText = lines[0].replace(/^#{1,4}\s+/, '');
        return `<h3 class="detail-heading">${headingText}</h3>`;
      }

      // Section title headers (e.g. "Other Details:", "Where We Stayed:")
      if (lines.length === 1 && /^(Other Details|Where We Stayed|Tips & Info|Hotel|Car Rental|Food|Highlights|Entry Fees?):\s*$/i.test(lines[0])) {
        return `<h3 class="detail-heading">${lines[0]}</h3>`;
      }

      // Standard paragraph with <br/> for single linebreaks inside
      const paraContent = lines.map(l => convertRatingsToStars(l)).join('<br/>');
      return `<p>${paraContent}</p>`;
    });

    htmlResult += htmlBlocks.join('\n');
  }

  return htmlResult.trim();
};

/**
 * Takes a base filename (e.g. 'europe/greece/2026/greece_1.jpeg') and an offset index (1, 2, 3...)
 * and automatically increments the trailing digit sequence in the filename so each image is unique.
 * Supports zero-padding ('01' -> '02') and standard numbers ('1' -> '2').
 */
const incrementFilename = (baseFilename, offsetIndex) => {
  if (!baseFilename || offsetIndex === 0) return baseFilename;

  const lastDot = baseFilename.lastIndexOf('.');
  const namePart = lastDot !== -1 ? baseFilename.slice(0, lastDot) : baseFilename;
  const extPart = lastDot !== -1 ? baseFilename.slice(lastDot) : '';

  // Find the last digit sequence in the namePart
  const match = namePart.match(/\d+(?=\D*$)/);
  if (!match) {
    return `${namePart}_${offsetIndex + 1}${extPart}`;
  }

  const origNumStr = match[0];
  const startIdx = match.index;
  const origNum = parseInt(origNumStr, 10);
  const newNum = origNum + offsetIndex;

  // Preserve zero-padding if original was zero-padded
  const newNumStr = origNumStr.startsWith('0') && origNumStr.length > 1
    ? String(newNum).padStart(origNumStr.length, '0')
    : String(newNum);

  const newNamePart = namePart.slice(0, startIdx) + newNumStr + namePart.slice(startIdx + origNumStr.length);
  return `${newNamePart}${extPart}`;
};

const AdminStudio = () => {
  const [passcode, setPasscode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  const [activeTab, setActiveTab] = useState('placedetails'); // 'placedetails' | 'json'

  // Existing records state
  const [existingItems, setExistingItems] = useState([]);
  const [selectedRecordKey, setSelectedRecordKey] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);

  // Form state
  const [region, setRegion] = useState('');
  const [title, setTitle] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [description, setDescription] = useState('');
  const [descIsFirstLineHeading, setDescIsFirstLineHeading] = useState(false);
  const [descIsBulletList, setDescIsBulletList] = useState(false);
  
  // Page Recommendation Star Rating (1 to 5 Stars)
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  // Flag to explicitly allow saving summary card to 'places' DB table (default: false)
  const [saveToPlacesTable, setSaveToPlacesTable] = useState(false);

  // Carousel sub-pages items (imageBlock) with collapsible state
  const [imageBlock, setImageBlock] = useState([]);
  const [collapsedImageBlocks, setCollapsedImageBlocks] = useState({});

  // Days itinerary with date of visit and collapsible state
  const [days, setDays] = useState([
    { title: 'Day 1: Arrival & Exploration', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }
  ]);
  const [collapsedDays, setCollapsedDays] = useState({});

  // Desired image counts per day for batch generation
  const [imageCounts, setImageCounts] = useState({});

  // Common blocks state with date and images
  const [commonBlocks, setCommonBlocks] = useState([
    { title: 'Where We Stayed (Airbnb / Hotel)', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] },
    { title: 'Local Tips & Car Rental', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }
  ]);

  // Desired image counts per common block for batch generation
  const [commonBlockImageCounts, setCommonBlockImageCounts] = useState({});

  // JSON import string
  const [jsonText, setJsonText] = useState('');

  // Status message
  const [status, setStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const savedCode = sessionStorage.getItem('adminPasscode');
    if (savedCode === DEFAULT_PASSCODE) {
      setPasscode(savedCode);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchExistingItemsList();
    }
  }, [isAuthenticated]);

  const fetchExistingItemsList = async () => {
    setIsLoadingList(true);
    try {
      const res = await fetch('/api/admin/list');
      if (res.ok) {
        const data = await res.json();
        setExistingItems(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching itineraries list:', err);
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleSelectRecord = async (e) => {
    const val = e.target.value;
    setSelectedRecordKey(val);
    setStatus(null);
    setSaveToPlacesTable(false);

    if (!val) {
      handleResetForm();
      return;
    }

    const [recRegion, recTitle] = val.split(':::');
    if (!recRegion || !recTitle) return;

    setIsLoadingRecord(true);
    try {
      const res = await fetch(`/api/placedetails/${encodeURIComponent(recRegion)}/${encodeURIComponent(recTitle)}`);
      if (!res.ok) {
        throw new Error('Failed to fetch existing place details');
      }
      const data = await res.json();

      setRegion(data.region || recRegion);
      setTitle(data.title || recTitle);
      setMainImage(data.image || '');
      setDescription(data.description || '');
      setDescIsFirstLineHeading(false);
      setDescIsBulletList(false);
      setRating(data.rating || 0);
      setHoverRating(0);
      setImageBlock(data.imageBlock || []);
      setCollapsedImageBlocks({});

      if (data.days && Array.isArray(data.days) && data.days.length > 0) {
        setDays(data.days.map(d => {
          let dayImgs = [''];
          if (Array.isArray(d.images) && d.images.length > 0) {
            dayImgs = d.images;
          } else if (d.images && Object.values(d.images).length > 0) {
            dayImgs = Object.values(d.images);
          }
          return {
            title: d.title || '',
            date: d.date || '',
            description: d.description || '',
            isFirstLineHeading: false,
            isBulletList: false,
            images: dayImgs
          };
        }));
      } else {
        setDays([{ title: 'Day 1: ', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }]);
      }
      setCollapsedDays({});
      setImageCounts({});

      if (data.commonBlocks && Array.isArray(data.commonBlocks)) {
        setCommonBlocks(data.commonBlocks.map(b => {
          let blockImgs = [''];
          if (Array.isArray(b.images) && b.images.length > 0) {
            blockImgs = b.images;
          } else if (b.images && Object.values(b.images).length > 0) {
            blockImgs = Object.values(b.images);
          }
          return {
            title: b.title || '',
            date: b.date || '',
            description: b.description || '',
            isFirstLineHeading: false,
            isBulletList: false,
            images: blockImgs
          };
        }));
      } else {
        setCommonBlocks([]);
      }
      setCommonBlockImageCounts({});

      setStatus({
        type: 'success',
        message: `📂 Loaded existing itinerary for "${data.title}" (${data.region}). You can edit any fields below and save updates.`
      });
    } catch (err) {
      console.error('Error loading record details:', err);
      setStatus({ type: 'error', message: 'Failed to load record details: ' + err.message });
    } finally {
      setIsLoadingRecord(false);
    }
  };

  const handleResetForm = () => {
    setSelectedRecordKey('');
    setRegion('');
    setTitle('');
    setMainImage('');
    setDescription('');
    setDescIsFirstLineHeading(false);
    setDescIsBulletList(false);
    setRating(0);
    setHoverRating(0);
    setSaveToPlacesTable(false);
    setImageBlock([]);
    setCollapsedImageBlocks({});
    setDays([{ title: 'Day 1: Arrival & Exploration', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }]);
    setCollapsedDays({});
    setImageCounts({});
    setCommonBlocks([
      { title: 'Where We Stayed (Airbnb / Hotel)', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] },
      { title: 'Local Tips & Car Rental', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }
    ]);
    setCommonBlockImageCounts({});
    setStatus(null);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (passcode.trim() === DEFAULT_PASSCODE) {
      sessionStorage.setItem('adminPasscode', passcode.trim());
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      setAuthError('Invalid Admin Passcode. Access Denied.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPasscode');
    setIsAuthenticated(false);
    setPasscode('');
  };

  // ImageBlock Collapsible & Reorder Handlers
  const toggleImageBlockCollapse = (index) => {
    setCollapsedImageBlocks(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleExpandAllImageBlocks = () => {
    setCollapsedImageBlocks({});
  };

  const handleCollapseAllImageBlocks = () => {
    const newMap = {};
    imageBlock.forEach((_, idx) => {
      newMap[idx] = true;
    });
    setCollapsedImageBlocks(newMap);
  };

  const handleMoveImageBlockUp = (index) => {
    if (index === 0) return;
    const updated = [...imageBlock];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    setImageBlock(updated);
  };

  const handleMoveImageBlockDown = (index) => {
    if (index === imageBlock.length - 1) return;
    const updated = [...imageBlock];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    setImageBlock(updated);
  };

  // Day Collapsible & Reorder Handlers
  const toggleDayCollapse = (index) => {
    setCollapsedDays(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleExpandAllDays = () => {
    setCollapsedDays({});
  };

  const handleCollapseAllDays = () => {
    const newMap = {};
    days.forEach((_, idx) => {
      newMap[idx] = true;
    });
    setCollapsedDays(newMap);
  };

  const handleMoveDayUp = (index) => {
    if (index === 0) return;
    const updated = [...days];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    setDays(updated);
  };

  const handleMoveDayDown = (index) => {
    if (index === days.length - 1) return;
    const updated = [...days];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    setDays(updated);
  };

  // Day Handlers
  const handleAddDay = () => {
    setDays([...days, { title: `Day ${days.length + 1}: `, date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }]);
  };

  const handleRemoveDay = (index) => {
    setDays(days.filter((_, i) => i !== index));
  };

  const handleDayChange = (index, field, value) => {
    const updated = [...days];
    updated[index][field] = value;
    setDays(updated);
  };

  const handleAddDayImage = (dayIdx) => {
    const updated = [...days];
    if (!updated[dayIdx].images) updated[dayIdx].images = [];
    updated[dayIdx].images.push('');
    setDays(updated);
  };

  const handleDayImageChange = (dayIdx, imgIdx, val) => {
    const updated = [...days];
    updated[dayIdx].images[imgIdx] = val;
    setDays(updated);
  };

  const handleRemoveDayImage = (dayIdx, imgIdx) => {
    const updated = [...days];
    updated[dayIdx].images = updated[dayIdx].images.filter((_, i) => i !== imgIdx);
    setDays(updated);
  };

  const handleImageCountChange = (dayIdx, val) => {
    const num = parseInt(val, 10);
    setImageCounts(prev => ({
      ...prev,
      [dayIdx]: isNaN(num) || num < 1 ? '' : num
    }));
  };

  const handleGenerateAndPopulateImages = (dayIdx) => {
    const updated = [...days];
    const targetDay = updated[dayIdx];
    if (!targetDay) return;

    const count = parseInt(
      imageCounts[dayIdx] !== undefined
        ? imageCounts[dayIdx]
        : (targetDay.images && targetDay.images.length > 0 ? targetDay.images.length : 1),
      10
    );
    if (isNaN(count) || count < 1) return;

    const baseImage = (targetDay.images && targetDay.images[0] ? targetDay.images[0] : '').trim();

    const newImages = [];
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        newImages.push(baseImage || '');
      } else if (baseImage) {
        newImages.push(incrementFilename(baseImage, i));
      } else {
        newImages.push('');
      }
    }

    targetDay.images = newImages;
    setDays(updated);
  };

  // Common Blocks Handlers
  const handleAddCommonBlock = () => {
    setCommonBlocks([...commonBlocks, { title: '', date: '', description: '', isFirstLineHeading: false, isBulletList: false, images: [''] }]);
  };

  const handleCommonBlockChange = (index, field, value) => {
    const updated = [...commonBlocks];
    updated[index][field] = value;
    setCommonBlocks(updated);
  };

  const handleRemoveCommonBlock = (index) => {
    setCommonBlocks(commonBlocks.filter((_, i) => i !== index));
  };

  const handleAddCommonBlockImage = (blockIdx) => {
    const updated = [...commonBlocks];
    if (!updated[blockIdx].images) updated[blockIdx].images = [];
    updated[blockIdx].images.push('');
    setCommonBlocks(updated);
  };

  const handleCommonBlockImageChange = (blockIdx, imgIdx, val) => {
    const updated = [...commonBlocks];
    updated[blockIdx].images[imgIdx] = val;
    setCommonBlocks(updated);
  };

  const handleRemoveCommonBlockImage = (blockIdx, imgIdx) => {
    const updated = [...commonBlocks];
    updated[blockIdx].images = updated[blockIdx].images.filter((_, i) => i !== imgIdx);
    setCommonBlocks(updated);
  };

  const handleCommonBlockImageCountChange = (blockIdx, val) => {
    const num = parseInt(val, 10);
    setCommonBlockImageCounts(prev => ({
      ...prev,
      [blockIdx]: isNaN(num) || num < 1 ? '' : num
    }));
  };

  const handleGenerateAndPopulateCommonBlockImages = (blockIdx) => {
    const updated = [...commonBlocks];
    const targetBlock = updated[blockIdx];
    if (!targetBlock) return;

    const count = parseInt(
      commonBlockImageCounts[blockIdx] !== undefined
        ? commonBlockImageCounts[blockIdx]
        : (targetBlock.images && targetBlock.images.length > 0 ? targetBlock.images.length : 1),
      10
    );
    if (isNaN(count) || count < 1) return;

    const baseImage = (targetBlock.images && targetBlock.images[0] ? targetBlock.images[0] : '').trim();

    const newImages = [];
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        newImages.push(baseImage || '');
      } else if (baseImage) {
        newImages.push(incrementFilename(baseImage, i));
      } else {
        newImages.push('');
      }
    }

    targetBlock.images = newImages;
    setCommonBlocks(updated);
  };

  // ImageBlock Handlers (Nested Sub-Pages / Carousel)
  const handleAddImageBlock = () => {
    setImageBlock([...imageBlock, { title: '', image: '' }]);
  };

  const handleImageBlockChange = (index, field, value) => {
    const updated = [...imageBlock];
    updated[index][field] = value;
    setImageBlock(updated);
  };

  const handleRemoveImageBlock = (index) => {
    setImageBlock(imageBlock.filter((_, i) => i !== index));
  };

  // Helper to convert text to bullets directly in text
  const applyBulletPrefixToText = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    return lines.map(l => {
      const trimmed = l.trim();
      if (!trimmed || /^[-*•]\s+/.test(trimmed)) return l;
      return `- ${trimmed}`;
    }).join('\n');
  };

  // Helper to convert 1st line to heading directly in text
  const applyHeadingPrefixToText = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    if (lines.length === 0) return '';
    if (/^#{1,4}\s+/.test(lines[0].trim())) return text;
    lines[0] = `### ${lines[0].trim()}`;
    return lines.join('\n');
  };

  // Save to Database
  const handleSaveToDatabase = async () => {
    if (!region || !title) {
      setStatus({ type: 'error', message: 'Region and Title are required fields!' });
      return;
    }

    setIsSaving(true);
    setStatus(null);

    const formattedDescription = formatTextToHtml(description, {
      isFirstLineHeading: descIsFirstLineHeading,
      isBulletList: descIsBulletList
    });

    const formattedDays = days.map(d => ({
      title: d.title,
      date: d.date ? d.date.trim() : '',
      description: formatTextToHtml(d.description, {
        isFirstLineHeading: d.isFirstLineHeading,
        isBulletList: d.isBulletList
      }),
      images: (d.images || []).filter(img => img.trim().length > 0)
    }));

    const formattedCommonBlocks = commonBlocks.map(b => ({
      title: b.title ? b.title.trim() : '',
      date: b.date ? b.date.trim() : '',
      description: formatTextToHtml(b.description, {
        isFirstLineHeading: b.isFirstLineHeading,
        isBulletList: b.isBulletList
      }),
      images: (b.images || []).filter(img => img.trim().length > 0)
    })).filter(b => b.title.trim().length > 0);

    const payloadData = {
      region: region.trim(),
      title: title.trim(),
      image: mainImage.trim(),
      description: formattedDescription,
      rating: rating,
      imageBlock: imageBlock.filter(ib => ib.title && ib.title.trim().length > 0),
      days: formattedDays,
      commonBlocks: formattedCommonBlocks,
      saveToPlacesTable: saveToPlacesTable
    };

    try {
      const res = await fetch('/api/admin/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': passcode
        },
        body: JSON.stringify({
          type: 'placedetails',
          data: payloadData
        })
      });

      const resData = await res.json();
      if (res.ok && resData.success) {
        const extraMsg = saveToPlacesTable ? ' (and main places DB table)' : '';
        setStatus({ type: 'success', message: `Successfully updated/saved "${title}" (${region}) in MongoDB placedetails${extraMsg}!` });
        fetchExistingItemsList();
      } else {
        throw new Error(resData.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Save error:', err);
      setStatus({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  // Import JSON
  const handleImportJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed.region) setRegion(parsed.region);
      if (parsed.title) setTitle(parsed.title);
      if (parsed.image) setMainImage(parsed.image);
      if (parsed.description) setDescription(parsed.description);
      if (parsed.rating) setRating(parsed.rating);
      if (parsed.days && Array.isArray(parsed.days)) setDays(parsed.days);
      if (parsed.commonBlocks && Array.isArray(parsed.commonBlocks)) setCommonBlocks(parsed.commonBlocks);
      if (parsed.imageBlock && Array.isArray(parsed.imageBlock)) setImageBlock(parsed.imageBlock);

      setStatus({ type: 'success', message: 'JSON successfully imported into form fields!' });
    } catch (e) {
      setStatus({ type: 'error', message: 'Invalid JSON format: ' + e.message });
    }
  };

  // Render Login Lock Screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="admin-lock-screen">
          <div className="lock-card">
            <h2>🔒 Admin Studio Lock</h2>
            <p>Enter your secret admin passcode to access the Travelogue Studio.</p>

            {authError && <div className="auth-error-banner">{authError}</div>}

            <form onSubmit={handleLogin}>
              <input
                type="password"
                placeholder="Enter Admin Passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className="passcode-input"
                autoFocus
              />
              <button type="submit" className="btn-primary-lg" style={{ marginTop: '16px', width: '100%' }}>
                Unlock Studio
              </button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="admin-studio-container">
        <header className="studio-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>Travelogue Data Input Studio</h1>
            <button className="btn-secondary" onClick={handleLogout}>🔒 Lock Studio</button>
          </div>
          <p>Private Content Management Suite for Shantanu & Family.</p>
        </header>

        <div className="studio-tabs">
          <button
            className={`tab-btn ${activeTab === 'placedetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('placedetails')}
          >
            Form Editor
          </button>
          <button
            className={`tab-btn ${activeTab === 'json' ? 'active' : ''}`}
            onClick={() => setActiveTab('json')}
          >
            JSON Import / Export
          </button>
        </div>

        {status && (
          <div className={`status-banner ${status.type}`}>
            {status.message}
          </div>
        )}

        {activeTab === 'placedetails' && (
          <div className="studio-form">
            {/* EXISTING RECORDS SELECTOR */}
            <section className="form-card record-selector-card">
              <div className="card-header-flex">
                <h2>📂 Edit Existing Itinerary or Create New</h2>
                <button type="button" className="btn-secondary" onClick={handleResetForm}>
                  ➕ Create New Blank Form
                </button>
              </div>

              <div className="form-group full-width">
                <label>Select an Existing Destination to Load & Edit:</label>
                <select
                  value={selectedRecordKey}
                  onChange={handleSelectRecord}
                  disabled={isLoadingList || isLoadingRecord}
                  className="record-select-input"
                >
                  <option value="">-- Choose an existing itinerary to edit --</option>
                  {existingItems.map((item, idx) => (
                    <option key={idx} value={`${item.region}:::${item.title}`}>
                      [{item.region}] - {item.title}
                    </option>
                  ))}
                </select>
                {isLoadingRecord && <div className="loading-subtext">Loading itinerary details...</div>}
              </div>
            </section>

            <section className="form-card">
              <h2>1. Basic Information</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Region / Country *</label>
                  <input
                    type="text"
                    placeholder="e.g. Italy, NorthEast, Costa Rica..."
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Place Title / Destination *</label>
                  <input
                    type="text"
                    placeholder="e.g. Amalfi Coast, Grand Canyon..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Main Cover Image Filename (GitHub CDN)</label>
                  <input
                    type="text"
                    placeholder="e.g. amalfi_banner.jpg or italy/amalfi.jpg"
                    value={mainImage}
                    onChange={(e) => setMainImage(e.target.value)}
                  />
                  {mainImage && (
                    <div className="cdn-preview">
                      <span>GitHub CDN Preview:</span>
                      <img src={getCdnImageUrl(mainImage)} alt="Preview" onError={(e) => e.target.style.display='none'} />
                    </div>
                  )}
                </div>

                {/* RECOMMENDATION STAR RATING SELECTOR (1 TO 5 STARS) */}
                <div className="form-group full-width recommendation-picker-card">
                  <label style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a' }}>
                    Recommendation Rating (1 to 5 Stars):
                  </label>
                  <div className="star-picker">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isActive = star <= (hoverRating || rating);
                      return (
                        <span
                          key={star}
                          className={`star-picker-item ${isActive ? 'active' : 'inactive'}`}
                          onClick={() => setRating(star === rating ? 0 : star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          title={`${star} Star${star > 1 ? 's' : ''}`}
                        >
                          ★
                        </span>
                      );
                    })}
                    <span className="rating-num-display">
                      {rating > 0 ? `${rating} out of 5 Stars` : 'No rating selected'}
                    </span>
                    {rating > 0 && (
                      <button type="button" className="btn-link" style={{ marginLeft: '12px', fontSize: '0.8rem' }} onClick={() => setRating(0)}>
                        Clear Rating
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Overall Description (Free Plain Text or HTML)</span>
                  </label>

                  {/* FORMATTING FLAGS TOOLBAR */}
                  <div className="formatting-toolbar-row" style={{ display: 'flex', gap: '16px', alignItems: 'center', margin: '4px 0 8px 0', background: '#f1f5f9', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>Formatting Flags:</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={descIsFirstLineHeading}
                        onChange={(e) => setDescIsFirstLineHeading(e.target.checked)}
                      />
                      <span>🏷️ 1st Line as Heading</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={descIsBulletList}
                        onChange={(e) => setDescIsBulletList(e.target.checked)}
                      />
                      <span>• Format Content as Bullet List</span>
                    </label>

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                      <button type="button" className="btn-link-action" onClick={() => setDescription(applyHeadingPrefixToText(description))}>
                        ⚡ Make 1st Line Heading
                      </button>
                      <button type="button" className="btn-link-action" onClick={() => setDescription(applyBulletPrefixToText(description))}>
                        ⚡ Convert to Bullets
                      </button>
                    </div>
                  </div>

                  <textarea
                    rows={4}
                    placeholder="Type narrative paragraphs... Double line-breaks auto-convert into clean <p> paragraphs. Ratings (e.g. Rating: 4.5/5) convert into gold stars."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                {/* OPTIONAL FLAG: ALSO SAVE TO MAIN 'PLACES' DB TABLE (DEFAULT OFF) */}
                <div className="form-group full-width" style={{ background: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', marginTop: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', color: '#1a237e', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={saveToPlacesTable}
                      onChange={(e) => setSaveToPlacesTable(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Also save summary card to main 'places' DB collection (Publish to Region overview page)</span>
                  </label>
                  <p style={{ margin: '4px 0 0 28px', fontSize: '0.82rem', color: '#64748b' }}>
                    (Disabled/OFF by default. Unchecked means it will ONLY save to the 'placedetails' collection.)
                  </p>
                </div>
              </div>
            </section>

            {/* NESTED IMAGE BLOCK CAROUSEL SECTION (COLLAPSIBLE & REORDERABLE) */}
            <section className="form-card">
              <div className="card-header-flex">
                <div>
                  <h2>2. Nested Sub-Page Image Carousel (`imageBlock` - {imageBlock.length} Items)</h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                    Add nested destinations or sub-pages that appear as clickable image cards (like Norway).
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn-secondary" onClick={handleExpandAllImageBlocks}>📂 Expand All</button>
                  <button type="button" className="btn-secondary" onClick={handleCollapseAllImageBlocks}>📁 Collapse All</button>
                  <button type="button" className="btn-secondary" onClick={handleAddImageBlock}>+ Add Image Block</button>
                </div>
              </div>

              {imageBlock.map((block, ibIdx) => {
                const isCollapsed = Boolean(collapsedImageBlocks[ibIdx]);

                return (
                  <div key={ibIdx} className={`sub-card ${isCollapsed ? 'collapsed-card' : ''}`}>
                    <div className="sub-card-header day-card-header" onClick={() => toggleImageBlockCollapse(ibIdx)}>
                      <span className="toggle-collapse-btn">
                        {isCollapsed ? '▶' : '▼'}
                      </span>

                      <div className="reorder-btn-group" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-reorder"
                          disabled={ibIdx === 0}
                          onClick={() => handleMoveImageBlockUp(ibIdx)}
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="btn-reorder"
                          disabled={ibIdx === imageBlock.length - 1}
                          onClick={() => handleMoveImageBlockDown(ibIdx)}
                          title="Move Down"
                        >
                          ▼
                        </button>
                      </div>

                      <input
                        type="text"
                        style={{ flex: 1 }}
                        value={block.title}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleImageBlockChange(ibIdx, 'title', e.target.value)}
                        placeholder="Sub-page Destination Title (e.g. Bergen, Geirangerfjord...)"
                      />
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImageBlock(ibIdx);
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {!isCollapsed && (
                      <div className="sub-card-body" style={{ marginTop: '12px' }}>
                        <div className="form-group">
                          <label>Image Filename (GitHub CDN)</label>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                              type="text"
                              placeholder="e.g. norway/bergen.jpg"
                              value={block.image}
                              onChange={(e) => handleImageBlockChange(ibIdx, 'image', e.target.value)}
                              className="wide-image-path-input"
                            />
                            {block.image && (
                              <img
                                src={getCdnImageUrl(block.image)}
                                alt="preview"
                                className="mini-preview"
                                onError={(e) => e.target.style.display='none'}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            {/* DAY-BY-DAY ITINERARIES (COLLAPSIBLE & REORDERABLE) */}
            <section className="form-card">
              <div className="card-header-flex">
                <h2>3. Day-by-Day Itineraries ({days.length} Days)</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" className="btn-secondary" onClick={handleExpandAllDays}>📂 Expand All</button>
                  <button type="button" className="btn-secondary" onClick={handleCollapseAllDays}>📁 Collapse All</button>
                  <button type="button" className="btn-secondary" onClick={handleAddDay}>+ Add Day</button>
                </div>
              </div>

              {days.map((day, dIdx) => {
                const isCollapsed = Boolean(collapsedDays[dIdx]);
                const dayImagesArray = day.images && day.images.length > 0 ? day.images : [''];
                const currentCountVal = imageCounts[dIdx] !== undefined ? imageCounts[dIdx] : dayImagesArray.length;

                return (
                  <div key={dIdx} className={`sub-card ${isCollapsed ? 'collapsed-card' : ''}`}>
                    <div className="sub-card-header day-card-header" onClick={() => toggleDayCollapse(dIdx)}>
                      <span className="toggle-collapse-btn">
                        {isCollapsed ? '▶' : '▼'}
                      </span>

                      <div className="reorder-btn-group" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn-reorder"
                          disabled={dIdx === 0}
                          onClick={() => handleMoveDayUp(dIdx)}
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="btn-reorder"
                          disabled={dIdx === days.length - 1}
                          onClick={() => handleMoveDayDown(dIdx)}
                          title="Move Down"
                        >
                          ▼
                        </button>
                      </div>

                      <input
                        type="text"
                        className="day-title-input"
                        style={{ flex: 2 }}
                        value={day.title}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleDayChange(dIdx, 'title', e.target.value)}
                        placeholder="Day Title (e.g. Day 1: Arrival)"
                      />
                      <input
                        type="text"
                        className="day-date-input"
                        style={{ flex: 1 }}
                        value={day.date || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleDayChange(dIdx, 'date', e.target.value)}
                        placeholder="Date of Visit (e.g. May 12, 2024)"
                      />
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveDay(dIdx);
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    {!isCollapsed && (
                      <div className="sub-card-body" style={{ marginTop: '12px' }}>
                        {/* FORMATTING FLAGS TOOLBAR FOR DAY */}
                        <div className="formatting-toolbar-row" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '8px', background: '#f1f5f9', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>Formatting Flags:</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(day.isFirstLineHeading)}
                              onChange={(e) => handleDayChange(dIdx, 'isFirstLineHeading', e.target.checked)}
                            />
                            <span>🏷️ 1st Line as Heading</span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(day.isBulletList)}
                              onChange={(e) => handleDayChange(dIdx, 'isBulletList', e.target.checked)}
                            />
                            <span>• Format Content as Bullet List</span>
                          </label>

                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            <button type="button" className="btn-link-action" onClick={() => handleDayChange(dIdx, 'description', applyHeadingPrefixToText(day.description))}>
                              ⚡ Make 1st Line Heading
                            </button>
                            <button type="button" className="btn-link-action" onClick={() => handleDayChange(dIdx, 'description', applyBulletPrefixToText(day.description))}>
                              ⚡ Convert to Bullets
                            </button>
                          </div>
                        </div>

                        <textarea
                          rows={4}
                          placeholder="Day narrative or Other Details... Ratings like 'Rating: 4.5/5' auto-convert into gold stars! Check the flags above to format 1st line as heading or convert to bullet list."
                          value={day.description}
                          onChange={(e) => handleDayChange(dIdx, 'description', e.target.value)}
                        />

                        <div className="image-inputs">
                          <label style={{ marginBottom: '8px' }}>Day Image Filenames (GitHub CDN)</label>

                          <div className="image-gen-control-row">
                            <div className="image-gen-input-group">
                              <label>How many image boxes for Day {dIdx + 1}?</label>
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={currentCountVal}
                                onChange={(e) => handleImageCountChange(dIdx, e.target.value)}
                                className="image-count-input"
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-generate-images"
                              onClick={() => handleGenerateAndPopulateImages(dIdx)}
                              title="Auto-create image boxes and populate incremented filenames based on 1st image"
                            >
                              ✨ Generate & Auto-Populate {currentCountVal} Image Boxes
                            </button>
                          </div>

                          {dayImagesArray.map((img, iIdx) => (
                            <div key={iIdx} className="img-input-row">
                              <input
                                type="text"
                                placeholder="e.g. europe/greece/2026/greece_1.jpeg"
                                value={img}
                                onChange={(e) => handleDayImageChange(dIdx, iIdx, e.target.value)}
                                className="wide-image-path-input"
                              />
                              {img && (
                                <img
                                  src={getCdnImageUrl(img)}
                                  alt="preview"
                                  className="mini-preview"
                                  onError={(e) => e.target.style.display='none'}
                                />
                              )}
                              <button type="button" className="btn-danger" style={{ padding: '2px 8px' }} onClick={() => handleRemoveDayImage(dIdx, iIdx)}>&times;</button>
                            </div>
                          ))}

                          <div style={{ display: 'flex', gap: '16px', marginTop: '10px', alignItems: 'center' }}>
                            <button type="button" className="btn-link" onClick={() => handleAddDayImage(dIdx)}>+ Add 1 Single Image Box</button>
                            <button type="button" className="btn-link" onClick={() => handleGenerateAndPopulateImages(dIdx)}>⚡ Re-Populate & Increment Numbers</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>

            {/* COMMON BLOCKS (TIPS, STAYS, EATS, OTHER DETAILS) */}
            <section className="form-card">
              <div className="card-header-flex">
                <h2>4. Common Blocks (Tips, Stays, Eats, Other Details)</h2>
                <button type="button" className="btn-secondary" onClick={handleAddCommonBlock}>+ Add Block</button>
              </div>

              {commonBlocks.map((block, bIdx) => {
                const blockImagesArray = block.images && block.images.length > 0 ? block.images : [''];
                const currentBlockImgCount = commonBlockImageCounts[bIdx] !== undefined
                  ? commonBlockImageCounts[bIdx]
                  : blockImagesArray.length;

                return (
                  <div key={bIdx} className="sub-card">
                    <div className="sub-card-header" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ flex: 2, minWidth: '220px' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                          Block Title:
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          value={block.title}
                          onChange={(e) => handleCommonBlockChange(bIdx, 'title', e.target.value)}
                          placeholder="Block Title (e.g. Where We Stayed, Other Details, Local Tips...)"
                        />
                      </div>

                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '2px' }}>
                          Date of Visit / Stay (Optional):
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          value={block.date || ''}
                          onChange={(e) => handleCommonBlockChange(bIdx, 'date', e.target.value)}
                          placeholder="e.g. May 15, 2024"
                        />
                      </div>

                      <button type="button" className="btn-danger" style={{ marginTop: '16px' }} onClick={() => handleRemoveCommonBlock(bIdx)}>
                        Remove Block
                      </button>
                    </div>

                    <div className="sub-card-body" style={{ marginTop: '12px' }}>
                      {/* FORMATTING FLAGS TOOLBAR FOR COMMON BLOCK */}
                      <div className="formatting-toolbar-row" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '8px', background: '#f1f5f9', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>Formatting Flags:</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={Boolean(block.isFirstLineHeading)}
                            onChange={(e) => handleCommonBlockChange(bIdx, 'isFirstLineHeading', e.target.checked)}
                          />
                          <span>🏷️ 1st Line as Heading</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: '600', color: '#1e293b', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={Boolean(block.isBulletList)}
                            onChange={(e) => handleCommonBlockChange(bIdx, 'isBulletList', e.target.checked)}
                          />
                          <span>• Format Content as Bullet List</span>
                        </label>

                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                          <button type="button" className="btn-link-action" onClick={() => handleCommonBlockChange(bIdx, 'description', applyHeadingPrefixToText(block.description))}>
                            ⚡ Make 1st Line Heading
                          </button>
                          <button type="button" className="btn-link-action" onClick={() => handleCommonBlockChange(bIdx, 'description', applyBulletPrefixToText(block.description))}>
                            ⚡ Convert to Bullets
                          </button>
                        </div>
                      </div>

                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                        Narrative & Details (Free Text or HTML):
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Block details... Ratings like 'Rating: 5/5' auto-convert into gold stars! Check flags above to turn 1st line into heading or convert text into a bullet list."
                        value={block.description}
                        onChange={(e) => handleCommonBlockChange(bIdx, 'description', e.target.value)}
                      />

                      <div className="image-inputs">
                        <label style={{ marginBottom: '8px', fontWeight: 600, color: '#1a237e' }}>
                          📸 Block Image Filenames (GitHub CDN)
                        </label>

                        <div className="image-gen-control-row">
                          <div className="image-gen-input-group">
                            <label>How many image boxes for this block?</label>
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={currentBlockImgCount}
                              onChange={(e) => handleCommonBlockImageCountChange(bIdx, e.target.value)}
                              className="image-count-input"
                            />
                          </div>
                          <button
                            type="button"
                            className="btn-generate-images"
                            onClick={() => handleGenerateAndPopulateCommonBlockImages(bIdx)}
                            title="Auto-create image boxes and populate incremented filenames based on 1st image"
                          >
                            ✨ Generate & Auto-Populate {currentBlockImgCount} Image Boxes
                          </button>
                        </div>

                        {blockImagesArray.map((img, iIdx) => (
                          <div key={iIdx} className="img-input-row">
                            <input
                              type="text"
                              placeholder="e.g. europe/greece/2026/stay_1.jpeg"
                              value={img}
                              onChange={(e) => handleCommonBlockImageChange(bIdx, iIdx, e.target.value)}
                              className="wide-image-path-input"
                            />
                            {img && (
                              <img
                                src={getCdnImageUrl(img)}
                                alt="preview"
                                className="mini-preview"
                                onError={(e) => e.target.style.display='none'}
                              />
                            )}
                            <button type="button" className="btn-danger" style={{ padding: '2px 8px' }} onClick={() => handleRemoveCommonBlockImage(bIdx, iIdx)}>&times;</button>
                          </div>
                        ))}

                        <div style={{ display: 'flex', gap: '16px', marginTop: '10px', alignItems: 'center' }}>
                          <button type="button" className="btn-link" onClick={() => handleAddCommonBlockImage(bIdx)}>+ Add 1 Single Image Box</button>
                          <button type="button" className="btn-link" onClick={() => handleGenerateAndPopulateCommonBlockImages(bIdx)}>⚡ Re-Populate & Increment Numbers</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-primary-lg"
                disabled={isSaving}
                onClick={handleSaveToDatabase}
              >
                {isSaving ? 'Formatting & Saving to MongoDB...' : '🚀 Format HTML & Save Itinerary to MongoDB'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'json' && (
          <div className="studio-json-importer">
            <p>Paste an existing itinerary JSON object below to auto-fill form fields or review formatting:</p>
            <textarea
              rows={16}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder={`{\n  "region": "Italy",\n  "title": "Amalfi Coast",\n  "image": "amalfi.jpg",\n  "description": "Exploration of Amalfi...",\n  "days": []\n}`}
            />
            <div className="json-actions">
              <button type="button" className="btn-primary" onClick={handleImportJson}>
                Import JSON to Form
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminStudio;
